import { Injectable } from '@angular/core';

export interface PendingInviteContext {
  token: string;
  email: string;
}

export type ResolvedAuthFlow = 'registered' | 'new_user' | 'invited';

@Injectable({
  providedIn: 'root'
})
export class AuthFlowStateService {
  private readonly resolvedFlowStorageKey = 'resolved_auth_flow';
  private pendingInviteContext: PendingInviteContext | null = null;

  setPendingInviteContext(token: string, email: string): void {
    const normalizedToken = (token || '').trim();
    const normalizedEmail = (email || '').trim().toLowerCase();

    if (!normalizedToken || !normalizedEmail) {
      this.clearPendingInviteContext();
      return;
    }

    this.pendingInviteContext = {
      token: normalizedToken,
      email: normalizedEmail
    };
  }

  getPendingInviteContext(): PendingInviteContext | null {
    if (!this.pendingInviteContext) {
      return null;
    }

    const token = (this.pendingInviteContext.token || '').trim();
    const email = (this.pendingInviteContext.email || '').trim().toLowerCase();

    if (!token || !email) {
      this.clearPendingInviteContext();
      return null;
    }

    return { token, email };
  }

  getPendingInviteTarget(): string | null {
    const invite = this.getPendingInviteContext();
    if (!invite) {
      return null;
    }

    return '/onboarding';
  }

  setResolvedFlow(flow: ResolvedAuthFlow): void {
    if (!this.hasSessionStorage()) {
      return;
    }

    sessionStorage.setItem(this.resolvedFlowStorageKey, flow);
  }

  getResolvedFlow(): ResolvedAuthFlow | null {
    if (!this.hasSessionStorage()) {
      return null;
    }

    const raw = (sessionStorage.getItem(this.resolvedFlowStorageKey) || '').trim();
    if (raw === 'registered' || raw === 'new_user' || raw === 'invited') {
      return raw;
    }

    if (raw) {
      this.clearResolvedFlow();
    }

    return null;
  }

  clearResolvedFlow(): void {
    if (!this.hasSessionStorage()) {
      return;
    }

    sessionStorage.removeItem(this.resolvedFlowStorageKey);
  }

  clearPendingInviteContext(): void {
    this.pendingInviteContext = null;
  }

  private hasSessionStorage(): boolean {
    return typeof sessionStorage !== 'undefined';
  }
}